import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, Plus, ChevronRight, User, AlertCircle, ChevronDown, Paperclip, FileText, CheckCircle2, DollarSign, Settings, Trophy, ClipboardList, Info, Sparkles, Briefcase, Mail, Eye } from 'lucide-react';
import { LEAD_TYPES, DOCUMENT_STATUSES, PREQUALIFICATION_STATUSES, AWARD_STATUSES } from '../../lib/constants';
import ProfileModal from './ProfileModal';
import { contactService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker } from '../ui';
import { toLocalDateString } from '../../utils/helpers';
import { useServices } from '../../hooks';

export default function NewPrequalificationModal({ onClose, onSave, managers, profiles, onAddProfile, sources, eventTypes, currentUser, defaultProfile = null }) {
  const { t } = useLanguage();
  const { services } = useServices();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [hasActivePrequal, setHasActivePrequal] = useState(false);
  const [activePrequalData, setActivePrequalData] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [showContactCreator, setShowContactCreator] = useState(false);
  const [formData, setFormData] = useState({
    manager: !['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead'].includes(currentUser?.role) ? currentUser?.name || '' : '',
    stage: 'New',
    guests: '',
    clientBudget: '',
    eventType: '',
    eventDate: '',
    inquiryDate: toLocalDateString(new Date()),
    notes: '',
    source: '',
    services: [],
    // Prequalification Type & Prequalification
    leadType: 'PREQUALIFICATION',
    contractType: '',
    siteBid: '',
    contactPerson: '',
    documentStatus: 'To be Received',
    submissionDate: '',
    earnestMoneyRequired: false,
    earnestMoneyValue: '',
    prequalificationStatus: 'New',
    statusRemarks: '',
    attachmentUrl: '',
    // Proposal / Tender Commercials & Strategy
    proposalValue: '',
    grossProfit: '',
    wht: '',
    gpPercent: '',
    belt: '',
    paymentTerms: '',
    mobilizationAdvance: '',
    contractDuration: '',
    noOfStaff: '',
    winningStrategy: '',
    followUpDate: '',
    awardStatus: 'In Process'
  });

  const [profileNotice, setProfileNotice] = useState(null);

  // Async search state
  const [searchedContacts, setSearchedContacts] = useState([]);
  const [searching, setSearching] = useState(false);

  // Auto-select defaultProfile on mount
  React.useEffect(() => {
    if (defaultProfile && !selectedContact) {
      setSelectedContact(defaultProfile);
      populateProfileDetails(defaultProfile);
      setStep(2); // Skip straight to the form
    }
  }, [defaultProfile, selectedContact]);

  // Debounced search effect
  React.useEffect(() => {
    const search = async () => {
      if (!contactSearch) {
        setSearchedContacts([]);
        return;
      }
      setSearching(true);
      try {
        const result = await contactService.getAll({ limit: 5, search: contactSearch });
        setSearchedContacts(result.data);
      } catch (e) {
        console.error(e);
      } finally {
        setSearching(false);
      }
    };

    const timer = setTimeout(search, 400);
    return () => clearTimeout(timer);
  }, [contactSearch]);

  const getContactName = (c) => {
    if (!c) return 'Unknown';
    if (c.clientName) return c.clientName;
    const full = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return full || c.name || 'Unknown';
  };

  const getContactPhone = (c) => {
    if (!c) return '';
    return c.mobile || c.phone || '';
  };

  const getContactInitial = (c) => {
    const name = getContactName(c);
    return name ? name[0].toUpperCase() : 'U';
  };

  const displayContacts = contactSearch ? searchedContacts : (profiles || []).slice(0, 5);

  const populateProfileDetails = async (profileObj) => {
    setProfileNotice(null);
    setHasActivePrequal(false);
    setActivePrequalData(null);
    try {
      // Fetch full profile details including previous prequalifications
      const fullProfile = await contactService.getById(profileObj.id);
      const prequalifications = fullProfile?.prequalifications || [];
      const isEnlisted = prequalifications.some((l) => l.prequalificationStatus === 'Cleared' || l.status === 'Cleared' || l.status === 'Enlisted') || fullProfile?.enlistedStatus === 'Enlisted';
      
      const activePrequal = prequalifications.find((l) => {
        const isTypeMatch = l.type === 'PREQUALIFICATION' || l.leadType === 'PREQUALIFICATION';
        const isDisqualified = l.status === 'Disqualified' || l.prequalificationStatus === 'Disqualified';
        const isCleared = l.status === 'Cleared' || l.status === 'Enlisted' || l.prequalificationStatus === 'Cleared';
        return isTypeMatch && !isDisqualified && !isCleared;
      });

      if (!isEnlisted && activePrequal) {
        setHasActivePrequal(true);
        setActivePrequalData(activePrequal);
        
        setProfileNotice({
          type: 'warning',
          message: `An active prequalification already exists for this profile (Status: ${activePrequal.status || activePrequal.prequalificationStatus}). You can still create a new one.`,
          prequalId: activePrequal.id
        });
      }

      if (isEnlisted) {
        const prequalLead = prequalifications.find((l) => l.prequalificationStatus === 'Cleared' || l.status === 'Cleared' || l.status === 'Enlisted') || prequalifications[0] || {};
        const serviceNames = (prequalLead.services || []).map((s) => typeof s === 'string' ? s : s.name);
        const sourceName = prequalLead.source?.name || (typeof prequalLead.source === 'string' ? prequalLead.source : '') || '';

        setFormData((prev) => ({
          ...prev,
          contactPerson: prequalLead.contactPerson || fullProfile?.contactPersonName || getContactName(fullProfile),
          source: sourceName || prev.source,
          services: serviceNames.length > 0 ? serviceNames : prev.services,
          documentStatus: prequalLead.documentStatus || prev.documentStatus,
          submissionDate: prequalLead.submissionDate ? toLocalDateString(prequalLead.submissionDate) : prev.submissionDate,
          earnestMoneyRequired: prequalLead.earnestMoneyRequired ?? prev.earnestMoneyRequired,
          earnestMoneyValue: prequalLead.earnestMoneyValue || prev.earnestMoneyValue,
          prequalificationStatus: 'Enlisted',
          statusRemarks: prequalLead?.statusRemarks || prev.statusRemarks,
          attachmentUrl: prequalLead?.attachmentUrl || prev.attachmentUrl,
          notes: prequalLead?.notes || prev.notes,
          proposalValue: prequalLead?.proposalValue || prev.proposalValue,
          grossProfit: prequalLead?.grossProfit || prev.grossProfit,
          wht: prequalLead?.wht || prev.wht,
          gpPercent: prequalLead?.gpPercent || prev.gpPercent,
          paymentTerms: prequalLead?.paymentTerms || prev.paymentTerms,
          mobilizationAdvance: prequalLead?.mobilizationAdvance || prev.mobilizationAdvance,
          contractDuration: prequalLead?.contractDuration || prev.contractDuration,
          noOfStaff: prequalLead?.noOfStaff || prev.noOfStaff,
          winningStrategy: prequalLead?.winningStrategy || prev.winningStrategy,
          followUpDate: prequalLead?.followUpDate ? toLocalDateString(prequalLead.followUpDate) : prev.followUpDate,
          awardStatus: prequalLead?.awardStatus || prev.awardStatus,
          leadType: 'PROPOSAL_TENDER'
        }));

        setProfileNotice({
          type: 'success',
          message: t('clientEnlistedMsg') || 'Client Profile is ENLISTED! Auto-selected Proposal / Tender Form with pre-filled prequalification details.'
        });
      } else {
        // If not enlisted, we are creating a fresh Prequalification.
        // We only pre-fill the contact person. 
        setFormData((prev) => ({
          ...prev,
          contactPerson: fullProfile?.contactPersonName || getContactName(fullProfile)
        }));
      }
    } catch (e) {
      console.error('Error fetching profile prequalifications:', e);
      setFormData((prev) => ({
        ...prev,
        contactPerson: profileObj?.contactPersonName || getContactName(profileObj)
      }));
    }
  };

  const handleContactSelect = (profile) => {
    setSelectedContact(profile);
    setStep(2);
    populateProfileDetails(profile);
  };

  const handleCreateContact = async (data) => {
    const newContact = await onAddProfile(data);
    if (newContact) {
      setSelectedContact(newContact);
      setShowContactCreator(false);
      setStep(2);
      populateProfileDetails(newContact);
    }
  };

  const handleProposalMoneyChange = (field, val) => {
    const updatedData = { ...formData, [field]: val };
    const propVal = Number(field === 'proposalValue' ? val : formData.proposalValue) || 0;
    const gpVal = Number(field === 'grossProfit' ? val : formData.grossProfit) || 0;
    if (propVal > 0 && gpVal >= 0) {
      updatedData.gpPercent = ((gpVal / propVal) * 100).toFixed(2);
    }
    setFormData(updatedData);
  };

  const handleSubmit = () => {
    if (!selectedContact) {
      setLocalError(t('contactRequired') || 'Profile is required.');
      return;
    }

    setLocalError(null);

    const propVal = Number(formData.proposalValue) || 0;
    const budgetVal = Number(formData.clientBudget) || propVal || 0;

    let finalStage = 'New';
    if (formData.leadType === 'PREQUALIFICATION') {
      finalStage = formData.prequalificationStatus || 'New';
    } else if (formData.leadType === 'PROPOSAL_TENDER') {
      finalStage = formData.awardStatus || 'In Process';
    }

    onSave({
      ...formData,
      stage: finalStage,
      contactId: selectedContact.id,
      profileId: selectedContact.id,
      clientName: getContactName(selectedContact),
      phone: getContactPhone(selectedContact),
      quotationAmount: propVal,
      clientBudget: budgetVal,
      guests: Number(formData.guests) || null,
      earnestMoneyValue: Number(formData.earnestMoneyValue) || 0,
      proposalValue: propVal,
      grossProfit: Number(formData.grossProfit) || 0,
      wht: Number(formData.wht) || 0,
      gpPercent: Number(formData.gpPercent) || 0,
      mobilizationAdvance: Number(formData.mobilizationAdvance) || 0,
      noOfStaff: Number(formData.noOfStaff) || 0
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {showContactCreator && (
        <ProfileModal
          onClose={() => setShowContactCreator(false)}
          onSave={handleCreateContact}
        />
      )}
      <div className="bg-white w-full max-w-2xl max-h-[95vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center shrink-0">
          <h3 className="font-bold text-gray-800 text-lg">
            {step === 1 ? t('step1SelectProfile') : t('step2LeadDetails')}
          </h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
          </button>
        </div>
        {localError && (
          <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{localError}</span>
            <button onClick={() => setLocalError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="p-6 overflow-y-auto">
          {step === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                {t('leadProfileDescription')}
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  className="w-full pl-10 p-3 border rounded-xl bg-gray-50 outline-none"
                  placeholder={t('searchByNameOrPhone')}
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {searching && <p className="text-center text-xs text-gray-500">{t('searching')}</p>}
                {displayContacts.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => handleContactSelect(c)}
                    className="flex items-center justify-between p-3 hover:bg-blue-50 rounded-lg cursor-pointer border border-transparent hover:border-blue-100 transition-all"
                  >
                    <div className="flex items-start gap-3 w-full overflow-hidden">
                      <div className="w-10 h-10 shrink-0 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                        {getContactInitial(c)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-bold text-gray-900 text-sm truncate">
                            {c.clientName || getContactName(c)}
                          </p>
                          <div className="flex items-center gap-2">
                            {c.status && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 shrink-0">
                                {c.status}
                              </span>
                            )}
                            {getContactPhone(c) && (
                              <span className="text-xs font-medium text-gray-600 shrink-0 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                                {getContactPhone(c)}
                              </span>
                            )}
                          </div>
                        </div>
                        {(c.contactPersonName || c.email) && (
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {c.contactPersonName && (
                              <div className="flex items-center gap-1.5 truncate">
                                <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <span className="truncate">{c.contactPersonName}</span>
                              </div>
                            )}
                            {c.email && (
                              <div className="flex items-center gap-1.5 truncate">
                                <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                <span className="truncate">{c.email}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                ))}
                {!searching && displayContacts.length === 0 && contactSearch && (
                  <p className="text-center text-sm text-gray-400 py-2">{t('noContacts')}</p>
                )}
              </div>
              <button
                onClick={() => setShowContactCreator(true)}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> {t('createNewProfile')}
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Selected Profile Banner */}
              <div className="bg-blue-50/80 border border-blue-100 p-3 rounded-xl flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="font-bold text-blue-900 text-sm">
                    {t('clientProfile') || 'Client Profile'}: {getContactName(selectedContact)}
                  </span>
                </div>
                <button
                  onClick={() => setStep(1)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold underline"
                >
                  {t('change')}
                </button>
              </div>

              {profileNotice && (
                <div className={`p-3 rounded-xl text-xs font-semibold flex items-center justify-between animate-fade-in ${
                  profileNotice.type === 'success'
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                    : profileNotice.type === 'warning'
                      ? 'bg-amber-50 border border-amber-200 text-amber-800'
                      : profileNotice.type === 'error'
                        ? 'bg-red-50 border border-red-200 text-red-800'
                        : 'bg-blue-50 border border-blue-200 text-blue-800'
                }`}>
                  <span className="flex items-center gap-2">
                    {profileNotice.type === 'success' ? <Sparkles className="w-4 h-4 shrink-0" /> : profileNotice.type === 'warning' ? <AlertCircle className="w-4 h-4 shrink-0" /> : profileNotice.type === 'error' ? <AlertCircle className="w-4 h-4 shrink-0" /> : <Info className="w-4 h-4 shrink-0" />}
                    {profileNotice.message}
                  </span>
                  <div className="flex items-center gap-2 ml-2 shrink-0">
                    {profileNotice.prequalId && (
                      <button
                        onClick={() => { window.open(window.location.pathname + `?prequalificationId=${profileNotice.prequalId}`, '_blank'); }}
                        className="flex items-center gap-1 px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-bold transition-colors"
                      >
                        <Eye className="w-3 h-3" />
                        View
                      </button>
                    )}
                    <button onClick={() => setProfileNotice(null)} className="text-gray-400 hover:text-gray-600">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Form Content Based on Selected Prequalification Type */}
              {formData.leadType === 'PREQUALIFICATION' ? (
                <div className="space-y-5">
                  {/* Section 1: Prequalification Information */}
                  <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/30 space-y-3">
                    <h4 className="font-bold text-xs text-blue-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-blue-200 pb-2">
                      <ClipboardList className="w-4 h-4" /> 1. {t('prequalificationInfo') || 'Prequalification Information'}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600">{t('source')} *</label>
                        <Dropdown>
                          <DropdownTrigger>
                            <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px]">
                              {formData.source || t('selectSource')}
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full">
                            <DropdownMenu
                              selectedKeys={new Set([formData.source])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setFormData({ ...formData, source: Array.from(keys)[0] })}
                            >
                              {sources?.length ? sources.map((s) => (
                                <DropdownItem key={s.id} id={s.name}>
                                  {s.name}
                                </DropdownItem>
                              )) : <DropdownItem id="">{t('noSourcesAvailable') || 'No Sources'}</DropdownItem>}
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">{t('services') || 'Services'}</label>
                        <Dropdown>
                          <DropdownTrigger>
                            <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px]">
                              {formData.services.length > 0 ? `${formData.services.length} selected` : 'Select Services'}
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full">
                            <DropdownMenu
                              selectedKeys={new Set(formData.services)}
                              selectionMode="multiple"
                              onSelectionChange={(keys) => setFormData({ ...formData, services: Array.from(keys) })}
                            >
                              {services?.length ? services.map((s) => (
                                <DropdownItem key={s.id} id={s.name}>
                                  {s.name}
                                </DropdownItem>
                              )) : <DropdownItem id="" isDisabled>No Services Available</DropdownItem>}
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600">{t('contactPerson') || 'Contact Person'}</label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                          value={formData.contactPerson}
                          onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                          placeholder="e.g. John Doe (Procurement Manager)"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">{t('attachmentFileLink') || 'Attachment / File Link'}</label>
                        <div className="relative mt-1">
                          <Paperclip className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                          <input
                            type="text"
                            className="w-full pl-8 p-2 border rounded-lg text-sm bg-white"
                            value={formData.attachmentUrl}
                            onChange={(e) => setFormData({ ...formData, attachmentUrl: e.target.value })}
                            placeholder={t('urlOrDocReference') || 'URL or document reference'}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Process Details */}
                  <div className="border border-amber-200 rounded-xl p-4 bg-amber-50/30 space-y-3">
                    <h4 className="font-bold text-xs text-amber-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-amber-200 pb-2">
                      <Settings className="w-4 h-4" /> 2. {t('processDetails') || 'Process Details'}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600">{t('documentStatus') || 'Document Status'}</label>
                        <Dropdown>
                          <DropdownTrigger>
                            <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px]">
                              {formData.documentStatus}
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full">
                            <DropdownMenu
                              selectedKeys={new Set([formData.documentStatus])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setFormData({ ...formData, documentStatus: Array.from(keys)[0] })}
                            >
                              {DOCUMENT_STATUSES.map((ds) => (
                                <DropdownItem key={ds} id={ds}>
                                  {ds}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">{t('submissionDate') || 'Submission Date'}</label>
                        <DatePicker
                          className="mt-1"
                          value={formData.submissionDate}
                          onChange={(e) => setFormData({ ...formData, submissionDate: e.target.value })}
                          placement="top"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                      <div>
                        <label className="text-xs font-bold text-gray-600 block mb-1">{t('earnestMoney') || 'Earnest Money'}</label>
                        <div className="flex items-center gap-4 border border-gray-300 rounded-lg p-2 bg-white min-h-[40px]">
                          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold">
                            <input
                              type="radio"
                              name="earnestMoneyRequired"
                              checked={!formData.earnestMoneyRequired}
                              onChange={() => setFormData({ ...formData, earnestMoneyRequired: false, earnestMoneyValue: '' })}
                              className="text-blue-600"
                            />
                            {t('notRequired') || 'Not Required'}
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-blue-700">
                            <input
                              type="radio"
                              name="earnestMoneyRequired"
                              checked={formData.earnestMoneyRequired}
                              onChange={() => setFormData({ ...formData, earnestMoneyRequired: true })}
                              className="text-blue-600"
                            />
                            {t('required') || 'Required'}
                          </label>
                        </div>
                      </div>

                      {formData.earnestMoneyRequired && (
                        <div>
                          <label className="text-xs font-bold text-gray-600">Earnest Money Value (PKR)</label>
                          <input
                            type="number"
                            className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                            value={formData.earnestMoneyValue}
                            onChange={(e) => setFormData({ ...formData, earnestMoneyValue: e.target.value })}
                            placeholder="Amount in PKR"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 3: Outcome / Status */}
                  <div className="border border-green-200 rounded-xl p-4 bg-green-50/30 space-y-3">
                    <h4 className="font-bold text-xs text-green-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-green-200 pb-2">
                      <Trophy className="w-4 h-4" /> 3. {t('outcomeAssignee') || 'Outcome & Assignee'}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">


                      {['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead'].includes(currentUser?.role) ? (
                        <div>
                          <label className="text-xs font-bold text-gray-600">{t('manager')}</label>
                          <Dropdown>
                            <DropdownTrigger>
                              <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px]">
                                {formData.manager || (t('autoAssign') || 'Auto-Assign (Based on Rules)')}
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full">
                              <DropdownMenu
                                selectedKeys={new Set([formData.manager])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setFormData({ ...formData, manager: Array.from(keys)[0] })}
                              >
                                <DropdownItem id="">{t('autoAssign') || 'Auto-Assign'}</DropdownItem>
                                {([...new Set(managers || [])]).filter(m => m !== 'Unassigned').map((m) => (
                                  <DropdownItem key={m} id={m}>
                                    {m}
                                  </DropdownItem>
                                ))}
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                      ) : (
                        <div>
                          <label className="text-xs font-bold text-gray-600">{t('manager')}</label>
                          <div className="w-full p-2 border rounded-lg mt-1 bg-gray-100 text-gray-700 text-sm font-medium min-h-[40px] flex items-center">
                            {currentUser?.name} (Auto-assigned)
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-600">{t('statusBoxRemarks') || 'Status Box / Remarks'}</label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                        value={formData.statusRemarks}
                        onChange={(e) => setFormData({ ...formData, statusRemarks: e.target.value })}
                        placeholder={t('remarksOrNotesStatus') || 'Remarks or notes regarding status'}
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-600">{t('notes')}</label>
                      <textarea
                        className="w-full p-2 border rounded-lg mt-1 text-sm bg-white h-16"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder={t('additionalDetails') || 'Additional details...'}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* Proposal / Tender Form */
                <div className="space-y-5">
                  {/* Section 1: Prequalification & Submission */}
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/40 space-y-3">
                    <h4 className="font-bold text-xs text-blue-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-200 pb-2">
                      <FileText className="w-4 h-4" /> 1. Prequalification & Submission Details
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600">{t('source')} *</label>
                        <Dropdown>
                          <DropdownTrigger>
                            <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px]">
                              {formData.source || t('selectSource')}
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full">
                            <DropdownMenu
                              selectedKeys={new Set([formData.source])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setFormData({ ...formData, source: Array.from(keys)[0] })}
                            >
                              {sources?.length ? sources.map((s) => (
                                <DropdownItem key={s.id} id={s.name}>
                                  {s.name}
                                </DropdownItem>
                              )) : <DropdownItem id="">{t('noSourcesAvailable') || 'No Sources'}</DropdownItem>}
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">{t('services') || 'Services'}</label>
                        <Dropdown>
                          <DropdownTrigger>
                            <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px]">
                              {formData.services.length > 0 ? `${formData.services.length} selected` : 'Select Services'}
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full">
                            <DropdownMenu
                              selectedKeys={new Set(formData.services)}
                              selectionMode="multiple"
                              onSelectionChange={(keys) => setFormData({ ...formData, services: Array.from(keys) })}
                            >
                              {services?.length ? services.map((s) => (
                                <DropdownItem key={s.id} id={s.name}>
                                  {s.name}
                                </DropdownItem>
                              )) : <DropdownItem id="" isDisabled>No Services Available</DropdownItem>}
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600">Document Status</label>
                        <Dropdown>
                          <DropdownTrigger>
                            <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px]">
                              {formData.documentStatus}
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full">
                            <DropdownMenu
                              selectedKeys={new Set([formData.documentStatus])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setFormData({ ...formData, documentStatus: Array.from(keys)[0] })}
                            >
                              {DOCUMENT_STATUSES.map((ds) => (
                                <DropdownItem key={ds} id={ds}>
                                  {ds}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">Submission Date</label>
                        <DatePicker
                          className="mt-1"
                          value={formData.submissionDate}
                          onChange={(e) => setFormData({ ...formData, submissionDate: e.target.value })}
                          placement="top"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">Contact Person</label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                          value={formData.contactPerson}
                          onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                          placeholder="Contact person name"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600">Status Box / Remarks</label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                          value={formData.statusRemarks}
                          onChange={(e) => setFormData({ ...formData, statusRemarks: e.target.value })}
                          placeholder="Remarks regarding submission"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">Attachment / File Link</label>
                        <div className="relative mt-1">
                          <Paperclip className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                          <input
                            type="text"
                            className="w-full pl-8 p-2 border rounded-lg text-sm bg-white"
                            value={formData.attachmentUrl}
                            onChange={(e) => setFormData({ ...formData, attachmentUrl: e.target.value })}
                            placeholder="URL or document reference"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Commercial Details */}
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/40 space-y-3">
                    <h4 className="font-bold text-xs text-amber-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-200 pb-2">
                      <DollarSign className="w-4 h-4" /> 2. Commercial Details
                    </h4>

                    {/* Earnest Money */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                      <div>
                        <label className="text-xs font-bold text-gray-600 block mb-1">Earnest Money</label>
                        <div className="flex items-center gap-4 border border-gray-300 rounded-lg p-2 bg-white min-h-[40px]">
                          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold">
                            <input
                              type="radio"
                              name="earnestMoneyRequiredProp"
                              checked={!formData.earnestMoneyRequired}
                              onChange={() => setFormData({ ...formData, earnestMoneyRequired: false, earnestMoneyValue: '' })}
                              className="text-blue-600"
                            />
                            Not Required
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-blue-700">
                            <input
                              type="radio"
                              name="earnestMoneyRequiredProp"
                              checked={formData.earnestMoneyRequired}
                              onChange={() => setFormData({ ...formData, earnestMoneyRequired: true })}
                              className="text-blue-600"
                            />
                            Required
                          </label>
                        </div>
                      </div>

                      {formData.earnestMoneyRequired && (
                        <div>
                          <label className="text-xs font-bold text-gray-600">Earnest Money Value (PKR)</label>
                          <input
                            type="number"
                            className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                            value={formData.earnestMoneyValue}
                            onChange={(e) => setFormData({ ...formData, earnestMoneyValue: e.target.value })}
                            placeholder="Amount in PKR"
                          />
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600">Proposal Value (PKR) *</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white font-semibold text-blue-900"
                          value={formData.proposalValue}
                          onChange={(e) => handleProposalMoneyChange('proposalValue', e.target.value)}
                          placeholder="e.g. 1500000"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">Gross Profit (PKR)</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                          value={formData.grossProfit}
                          onChange={(e) => handleProposalMoneyChange('grossProfit', e.target.value)}
                          placeholder="e.g. 300000"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">GP %</label>
                        <input
                          type="number"
                          step="0.01"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-emerald-50 border-emerald-300 font-bold text-emerald-800"
                          value={formData.gpPercent}
                          onChange={(e) => setFormData({ ...formData, gpPercent: e.target.value })}
                          placeholder="Auto calculated %"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600">WHT (Withholding Tax)</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                          value={formData.wht}
                          onChange={(e) => setFormData({ ...formData, wht: e.target.value })}
                          placeholder="WHT Amount or %"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">Payment Terms</label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                          value={formData.paymentTerms}
                          onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
                          placeholder="e.g. Net 30, 50% Advance"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">Mobilization Advance (PKR)</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                          value={formData.mobilizationAdvance}
                          onChange={(e) => setFormData({ ...formData, mobilizationAdvance: e.target.value })}
                          placeholder="Advance amount"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600">Contract Duration</label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                          value={formData.contractDuration}
                          onChange={(e) => setFormData({ ...formData, contractDuration: e.target.value })}
                          placeholder="e.g. 1 Year, 6 Months"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">No. of Staff</label>
                        <input
                          type="number"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                          value={formData.noOfStaff}
                          onChange={(e) => setFormData({ ...formData, noOfStaff: e.target.value })}
                          placeholder="Required staff count"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Strategy & Award */}
                  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/40 space-y-3">
                    <h4 className="font-bold text-xs text-green-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-200 pb-2">
                      <Trophy className="w-4 h-4" /> 3. Strategy & Award
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-gray-600">Award Status</label>
                        <Dropdown>
                          <DropdownTrigger>
                            <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px]">
                              {formData.awardStatus}
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            </button>
                          </DropdownTrigger>
                          <DropdownPopover className="w-full">
                            <DropdownMenu
                              selectedKeys={new Set([formData.awardStatus])}
                              selectionMode="single"
                              onSelectionChange={(keys) => setFormData({ ...formData, awardStatus: Array.from(keys)[0] })}
                            >
                              {AWARD_STATUSES.map((as) => (
                                <DropdownItem key={as} id={as}>
                                  {as}
                                </DropdownItem>
                              ))}
                            </DropdownMenu>
                          </DropdownPopover>
                        </Dropdown>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-600">Follow-up Date</label>
                        <DatePicker
                          className="mt-1"
                          value={formData.followUpDate}
                          onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                          placement="top"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-600">Winning Strategy</label>
                      <input
                        type="text"
                        className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                        value={formData.winningStrategy}
                        onChange={(e) => setFormData({ ...formData, winningStrategy: e.target.value })}
                        placeholder="Key strategy to win this proposal..."
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead'].includes(currentUser?.role) ? (
                        <div>
                          <label className="text-xs font-bold text-gray-600">{t('manager')}</label>
                          <Dropdown>
                            <DropdownTrigger>
                              <button type="button" className="w-full p-2 border border-gray-300 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px]">
                                {formData.manager || (t('autoAssign') || 'Auto-Assign (Based on Rules)')}
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover className="w-full">
                              <DropdownMenu
                                selectedKeys={new Set([formData.manager])}
                                selectionMode="single"
                                onSelectionChange={(keys) => setFormData({ ...formData, manager: Array.from(keys)[0] })}
                              >
                                <DropdownItem id="">{t('autoAssign') || 'Auto-Assign'}</DropdownItem>
                                {([...new Set(managers || [])]).filter(m => m !== 'Unassigned').map((m) => (
                                  <DropdownItem key={m} id={m}>
                                    {m}
                                  </DropdownItem>
                                ))}
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                      ) : (
                        <div>
                          <label className="text-xs font-bold text-gray-600">{t('manager')}</label>
                          <div className="w-full p-2 border rounded-lg mt-1 bg-gray-100 text-gray-700 text-sm font-medium min-h-[40px] flex items-center">
                            {currentUser?.name} (Auto-assigned)
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="text-xs font-bold text-gray-600">{t('notes')}</label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded-lg mt-1 text-sm bg-white"
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          placeholder="Additional notes..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-4 bg-gray-50 border-t flex justify-end gap-2 shrink-0">
          {step === 2 && (
            <>
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg font-medium"
              >
                {t('back')}
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-2 rounded-lg font-bold bg-green-600 hover:bg-green-700 text-white"
              >
                {t('createLead')}
              </button>
            </>
          )}
          {step === 1 && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg font-medium"
            >
              {t('cancel')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
